import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { buildInitialRecipeSelections } from '../../lib/recipeChoice';

const normalizeText = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeUnit = (unit = "") => {
  const value = String(unit || "").trim().toLowerCase();
  if (value === "gramo" || value === "gramos" || value === "gr") return "g";
  if (value === "mililitro" || value === "mililitros") return "ml";
  return value || "unidad";
};

export default function RecipeChoicePOS({ open, onClose, product, products = [], onAddToCart }) {
  const recipe = product?.config?.recipe || {};
  const baseItems = useMemo(
    () => (Array.isArray(recipe.baseItems) ? recipe.baseItems : []),
    [recipe.baseItems]
  );
  const choiceGroups = useMemo(
    () => (Array.isArray(recipe.choiceGroups) ? recipe.choiceGroups : []),
    [recipe.choiceGroups]
  );
  const choiceGroupsSignature = useMemo(
    () => choiceGroups.map((group) => [
      group.id || "",
      group.label || "",
      group.min || 0,
      group.max || 0,
      group.perPickQty || 0,
      group.unit || "",
      group.fromCategory || "",
      group.defaultProductId || "",
    ].join("|")).join("||"),
    [choiceGroups]
  );

  const insumosById = useMemo(() => {
    const map = new Map();
    (products || []).forEach((item) => {
      if (item?.id) map.set(item.id, item);
    });
    return map;
  }, [products]);

  const missingBaseItems = useMemo(() => (
    baseItems.filter((item) => {
      const id = item.productId || item.id;
      const insumo = id ? insumosById.get(id) : null;
      return !insumo || insumo.deleted === true;
    })
  ), [baseItems, insumosById]);

  const optionsByGroup = useMemo(() => (
    choiceGroups.map((group) => {
      const wantedUnit = normalizeUnit(group.unit || "unidad");
      const wantedCategory = normalizeText(group.fromCategory || "");
      return (products || [])
        .filter((item) => String(item.type || "").toLowerCase() === "insumo")
        .filter((item) => item.deleted !== true)
        .filter((item) => normalizeText(item.category) === wantedCategory)
        .filter((item) => normalizeUnit(item.unit) === wantedUnit)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es", { sensitivity: "base" }));
    })
  ), [choiceGroups, products]);

  const [selectedByGroup, setSelectedByGroup] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelectedByGroup(buildInitialRecipeSelections(choiceGroups, optionsByGroup));
  }, [open, product?.id, choiceGroupsSignature, optionsByGroup]);

  const canAdd = useMemo(() => {
    if (missingBaseItems.length > 0) return false;
    return choiceGroups.every((group, groupIndex) => {
      const picks = (selectedByGroup[groupIndex] || []).filter(Boolean);
      const min = Number(group.min || 0);
      const max = Math.max(Number(group.max || 0), min);
      if (picks.length < min) return false;
      if (picks.length > max) return false;
      return true;
    });
  }, [choiceGroups, selectedByGroup, missingBaseItems]);

  const handleSelect = (groupIndex, slotIndex, value) => {
    setSelectedByGroup((prev) => prev.map((groupValues, idx) => {
      if (idx !== groupIndex) return groupValues;
      const next = [...groupValues];
      next[slotIndex] = value;
      return next;
    }));
  };

  const handleAdd = () => {
    if (missingBaseItems.length > 0) {
      const names = missingBaseItems.map((item) => item.name || item.productId || item.id || "Insumo sin nombre");
      alert(`No se puede vender "${product?.name || "este producto"}" porque faltan estos insumos: ${names.join(", ")}. Créelos nuevamente o reemplázalos en la receta.`);
      return;
    }
    if (!canAdd) {
      alert("Completa las opciones obligatorias de la receta.");
      return;
    }

    const comps = [];
    const selectedLabels = [];

    for (const bi of baseItems) {
      const insumoId = bi.productId || bi.id;
      const insumo = insumoId ? insumosById.get(insumoId) : null;
      if (!insumo) continue;
      comps.push({
        insumoId: insumo.id,
        name: insumo.name,
        unit: normalizeUnit(insumo.unit || bi.unit || "unidad"),
        qtyPerUnit: Number(bi.qty || bi.quantity || 1),
        costPrice: Number(insumo.costPrice || 0),
      });
    }

    choiceGroups.forEach((group, groupIndex) => {
      const picks = (selectedByGroup[groupIndex] || []).filter(Boolean);
      picks.forEach((pickId) => {
        const insumo = insumosById.get(pickId);
        if (!insumo) return;
        selectedLabels.push(`${group.label}: ${insumo.name}`);
        comps.push({
          insumoId: insumo.id,
          name: insumo.name,
          unit: normalizeUnit(insumo.unit || group.unit || "unidad"),
          qtyPerUnit: Number(group.perPickQty || 1),
          costPrice: Number(insumo.costPrice || 0),
        });
      });
    });

    if (comps.length === 0) {
      alert("La receta no tiene insumos configurados.");
      return;
    }

    onAddToCart({
      id: product.id,
      name: selectedLabels.length > 0 ? `${product.name} - ${selectedLabels.join(" / ")}` : product.name,
      image: product.image || "",
      price: Number(product.price || 0),
      costPrice: comps.reduce((sum, component) => (
        sum + Number(component.costPrice || 0) * Number(component.qtyPerUnit || 0)
      ), 0),
      quantity: 1,
      recipeComponents: comps,
    });
    onClose();
  };

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-2xl rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Configurar {product?.name || "receta"}</h3>
            <p className="text-sm text-gray-500">Selecciona las opciones variables antes de agregar al carrito.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
        </div>

        <div className="space-y-4">
          {missingBaseItems.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              <strong>Receta incompleta.</strong> No se puede vender este producto porque faltan:{" "}
              {missingBaseItems.map((item) => item.name || item.productId || item.id || "Insumo sin nombre").join(", ")}.
              {" "}Crea nuevamente esos insumos o reemplázalos en la receta.
            </div>
          )}
          {choiceGroups.map((group, groupIndex) => {
            const groupOptions = optionsByGroup[groupIndex] || [];
            const slots = selectedByGroup[groupIndex] || [];
            const min = Number(group.min || 0);
            const max = Math.max(Number(group.max || 0), min, 1);

            return (
              <div key={group.id || groupIndex} className="rounded-lg border border-gray-200 p-4">
                <div className="mb-2">
                  <h4 className="font-semibold text-gray-900">{group.label || `Opción ${groupIndex + 1}`}</h4>
                  <p className="text-xs text-gray-500">
                    Elige {min === max ? max : `${min} a ${max}`} opción(es) de {group.fromCategory} · {group.perPickQty} {normalizeUnit(group.unit || "unidad")} por elección
                  </p>
                </div>

                {groupOptions.length === 0 ? (
                  <div className="rounded bg-yellow-50 px-3 py-2 text-sm text-yellow-700">
                    No hay insumos en la categoría {group.fromCategory} con unidad {normalizeUnit(group.unit || "unidad")}.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {Array.from({ length: max }).map((_, slotIndex) => (
                      <div key={slotIndex}>
                        <label className="mb-1 block text-sm text-gray-700">
                          {group.label} #{slotIndex + 1}
                          {slotIndex >= min ? " (opcional)" : ""}
                        </label>
                        <select
                          value={slots[slotIndex] || ""}
                          onChange={(e) => handleSelect(groupIndex, slotIndex, e.target.value)}
                          className="w-full rounded border px-3 py-2"
                        >
                          <option value="">Seleccionar...</option>
                          {groupOptions.map((option) => (
                            <option key={option.id} value={option.id}>
                              {option.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded bg-gray-100 px-4 py-2 hover:bg-gray-200">
            Cancelar
          </button>
          <button
            onClick={handleAdd}
            disabled={!canAdd}
            className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Agregar al carrito
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
